import clsx from "clsx";
import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { NameInput } from "@features/breakdown-v2/label-rule-wizard/components/name-input";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";

import { RawSegmentsEditor } from "./raw-segments-editor";
import { Table } from "./table";
import {
  type AdvancedBreakdownDescriptor,
  type AdvancedSegmentDescriptor,
} from "./types";
import { createEmptyAdvancedSegment } from "./utils";

export interface AdvancedSegmentToEdit {
  isNew: boolean;
  segment: AdvancedSegmentDescriptor | null;
}

interface Props {
  advancedBreakdown: AdvancedBreakdownDescriptor;
  isRaw: boolean;
  onChange: (value: AdvancedBreakdownDescriptor) => void;
  onEditSegment: (value: AdvancedSegmentToEdit) => void;
  onChangeRaw: (value: boolean) => void;
}

export const AdvancedLabelRuleWizard = ({
  advancedBreakdown,
  isRaw,
  onChange,
  onEditSegment,
  onChangeRaw,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();

  return (
    <>
      <div className="mb-6">
        <div className="text-xs uppercase">Breakdown name</div>
        <div className="mb-1.5">
          <NameInput
            value={advancedBreakdown.name}
            onChange={(name) => onChange({ ...advancedBreakdown, name })}
          />
          {advancedBreakdown.error && (
            <div className="text-xxs text-mars mt-1">
              *{advancedBreakdown.error}
            </div>
          )}
        </div>
      </div>
      <div className="mb-4">
        <div className="text-lg font-bold mb-2">Breakdown segments</div>
        <div className="flex items-center justify-between">
          {isRaw ? (
            <>
              <div></div>
              <SecondaryButton
                className="font-bold flex items-center gap-2"
                onClick={() => {
                  prettyLogEventPage({
                    domain: "Breakdown v2",
                    action: "onClick",
                    additionalActionInfo: "Switch to structured mode",
                  });

                  onChangeRaw(false);
                }}
              >
                <i className="fa-regular fa-block-quote text-cosmic" />
                VIEW STRUCTURED
              </SecondaryButton>
            </>
          ) : (
            <>
              <PrimaryButton
                className="font-bold flex items-center gap-2"
                onClick={() => {
                  prettyLogEventPage({
                    domain: "Breakdown v2",
                    action: "onClick",
                    additionalActionInfo: "Add new segment",
                  });

                  onEditSegment({
                    isNew: true,
                    segment: createEmptyAdvancedSegment(),
                  });
                }}
              >
                <i className="fa-regular fa-circle-plus" />
                NEW SEGMENT
              </PrimaryButton>
              {user.isAdmin && (
                <SecondaryButton
                  className="font-bold flex items-center gap-2"
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Breakdown v2",
                      action: "onClick",
                      additionalActionInfo: "Switch to raw mode",
                    });

                    onChangeRaw(true);
                  }}
                >
                  <i className="fa-regular fa-code text-cosmic" />
                  VIEW RAW
                </SecondaryButton>
              )}
            </>
          )}
        </div>
      </div>
      {isRaw ? (
        <RawSegmentsEditor
          segments={advancedBreakdown.segments}
          onChange={(segments) => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onChange",
              additionalActionInfo: "Update segment in raw mode",
              data: {
                newSegments: segments,
              },
            });

            onChange({ ...advancedBreakdown, segments });
          }}
        />
      ) : (
        <div
          className={clsx(
            "border border-gray rounded-lg overflow-auto max-h-[calc(100%-164px)]",
          )}
        >
          <Table
            segments={advancedBreakdown.segments}
            onChange={(segment) => {
              const segments = advancedBreakdown.segments.map((e) =>
                e.id === segment.id ? segment : e,
              );

              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onChange",
                additionalActionInfo: "Update segment in structured mode",
                data: {
                  newSegments: !segments,
                },
              });

              onChange({
                ...advancedBreakdown,
                segments,
              });
            }}
            onEdit={(segment) => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onClick",
                additionalActionInfo: "Open advanced edit segment modal",
              });

              onEditSegment({ isNew: false, segment });
            }}
            onDuplicate={(segment) => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onClick",
                additionalActionInfo: "Open advanced duplicate segment modal",
              });

              const allNames = advancedBreakdown.segments.map((e) => e.name);
              let name = `${segment.name} (copy)`;
              let idx = 2;

              while (allNames.includes(name)) {
                name = `${segment.name} (copy ${idx})`;
                idx += 1;
              }

              onEditSegment({
                isNew: true,
                segment: { ...cloneDeep(segment), id: uuidv4(), name },
              });
            }}
            onDelete={(segment) => {
              const segments = advancedBreakdown.segments.filter(
                (e) => e.id !== segment.id,
              );

              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onClick",
                additionalActionInfo: "Delete segment",
                data: {
                  newSegments: !segments,
                },
              });

              onChange({
                ...advancedBreakdown,
                segments,
              });
            }}
          />
        </div>
      )}
    </>
  );
};
